import React, { useMemo, useState } from 'react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TutorWorkingHoursSummary from '../tutor/TutorWorkingHoursSummary';
import TutorSessionDiscoveryMeta from '../tutor/TutorSessionDiscoveryMeta';
import TutorPublicVisibilityToggle from '../tutor/TutorPublicVisibilityToggle';
import HostTutorSpaceAssignmentField from './HostTutorSpaceAssignmentField';
import { HostTutorResponse } from '../../models/Tutor';
import { Space } from '../../models/Space';
import {
  hostTutorDisplayName,
  hostTutorStatusColor,
} from '../../utils/hostTutorDisplay';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { hasTutorWorkingHoursSchedule } from '../../utils/tutorWorkingHours';
import { resolveLanguageLabel } from '../../utils/languageOptions';
import { tutorSkillLabel } from '../../utils/tutorSkillI18n';

export interface HostTutorRosterCardProps {
  tutor: HostTutorResponse;
  isSelf: boolean;
  spaces?: Space[];
  onVisibilityChange?: (tutorId: string, visible: boolean) => void;
  visibilityUpdating?: boolean;
  onSpaceChange?: (tutorId: string, spaceId: string) => void;
  spaceUpdating?: boolean;
  onCardClick?: (tutorId: string) => void;
}

function isInteractiveCardTarget(target: EventTarget | null, cardRoot: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const interactive = target.closest(
    'button, a, input, textarea, select, [role="button"], [role="combobox"], [role="listbox"], .MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiBackdrop-root'
  );
  // Ignore the card shell itself when it uses role="button" for keyboard activation.
  return Boolean(interactive && interactive !== cardRoot);
}

const cardShellSx = (theme: Theme) => ({
  position: 'relative' as const,
  overflow: 'hidden',
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: alpha(theme.palette.background.paper, 0.72),
  backgroundImage: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 52%)`,
  transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.45),
    transform: 'translateY(-2px)',
    boxShadow: `0 14px 36px ${alpha(theme.palette.common.black, 0.28)}`,
  },
});

const HostTutorRosterCard: React.FC<HostTutorRosterCardProps> = ({
  tutor,
  isSelf,
  spaces = [],
  onVisibilityChange,
  visibilityUpdating = false,
  onSpaceChange,
  spaceUpdating = false,
  onCardClick,
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const displayName = hostTutorDisplayName(tutor);
  const skills = tutor.skills ?? [];
  const languages = tutor.languages ?? [];
  const lessonCount = tutor.lessonOptions?.length ?? 0;
  const reviewCount = tutor.reviewCount ?? 0;
  const avgReviewRating = tutor.avgReviewRating ?? 0;
  const hasScheduleDetails = Boolean(
    hasTutorWorkingHoursSchedule(tutor.workingHours) || tutor.overview?.trim()
  );

  const statusLabel = useMemo(() => {
    const key = (tutor.status || 'ACTIVE').toUpperCase();
    return t(`hostMyTutors.tutorStatus.${key}`, { defaultValue: key });
  }, [t, tutor.status]);

  const isPubliclyVisible = tutor.visible !== false;
  const visibilityOnHint = t(
    'hostMyTutors.publicVisibilityOnHint',
    'Students can find this tutor in public tutor search.'
  );
  const visibilityOffHint = t(
    'hostMyTutors.publicVisibilityOffHint',
    'Hidden tutors stay on your roster but do not appear in public search.'
  );

  return (
    <Box
      sx={(theme) => ({
        ...cardShellSx(theme),
        ...(onCardClick
          ? {
              cursor: 'pointer',
            }
          : {}),
      })}
      onClick={(event) => {
        if (!onCardClick || isInteractiveCardTarget(event.target, event.currentTarget)) {
          return;
        }
        onCardClick(tutor.tutorId);
      }}
      onKeyDown={(event) => {
        if (!onCardClick || (event.key !== 'Enter' && event.key !== ' ')) {
          return;
        }
        if (isInteractiveCardTarget(event.target, event.currentTarget)) {
          return;
        }
        event.preventDefault();
        onCardClick(tutor.tutorId);
      }}
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      data-tutor-id={tutor.tutorId}
    >
      <Stack spacing={1.75} sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Stack spacing={1} alignItems="center" sx={{ minWidth: 72, flexShrink: 0 }}>
            <Avatar
              src={tutor.profileImageUrl?.trim() || undefined}
              alt=""
              variant="rounded"
              sx={{
                width: 72,
                height: 72,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.22),
                color: 'primary.light',
                fontWeight: 700,
                fontSize: '1.25rem',
                border: 1,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
              }}
            >
              {tutorAvatarInitials(displayName)}
            </Avatar>
            <Stack spacing={0.25} sx={{ width: '100%' }}>
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                <EventAvailableRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('tutorSearch.statsConfirmedBookings', 'Bookings: {{count}}', {
                    count: tutor.bookingCount ?? 0,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                <GroupsRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('tutorSearch.statsStudents', 'Students: {{count}}', {
                    count: tutor.studentCount ?? 0,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.25, letterSpacing: '-0.01em' }}>
                  {displayName}
                  {isSelf ? (
                    <Typography component="span" variant="body2" color="primary.light" sx={{ ml: 0.75, fontWeight: 600 }}>
                      {t('hostMyTutors.youSuffix')}
                    </Typography>
                  ) : null}
                </Typography>
                <Box
                  sx={{ mt: 0.5 }}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <HostTutorSpaceAssignmentField
                    tutorId={tutor.tutorId}
                    spaceId={tutor.spaceId}
                    spaceName={tutor.spaceName}
                    spaces={spaces}
                    disabled={spaceUpdating}
                    onSpaceChange={onSpaceChange}
                  />
                </Box>
                {tutor.headline?.trim() ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
                    {tutor.headline.trim()}
                  </Typography>
                ) : null}
              </Box>
              {onVisibilityChange ? (
                <TutorPublicVisibilityToggle
                  variant="icon"
                  visible={isPubliclyVisible}
                  disabled={visibilityUpdating}
                  onChange={(checked) => onVisibilityChange(tutor.tutorId, checked)}
                  label={t('hostMyTutors.publicVisibilityLabel', 'Public search visibility')}
                  onHint={visibilityOnHint}
                  offHint={visibilityOffHint}
                />
              ) : !isPubliclyVisible ? (
                <Tooltip title={visibilityOffHint}>
                  <Box
                    sx={(theme) => ({
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1.25,
                      bgcolor: alpha(theme.palette.text.disabled, 0.1),
                      color: 'text.disabled',
                      border: 1,
                      borderColor: 'divider',
                    })}
                    aria-label={t('hostMyTutors.hiddenFromSearch', 'Hidden from search')}
                  >
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Stack direction="row" spacing={0} sx={{ display: 'inline-flex' }}>
                {reviewCount > 0 ? (
                  <StarRoundedIcon sx={{ color: '#F8C14A', fontSize: 18 }} />
                ) : (
                  Array.from({ length: 5 }, (_, i) => (
                    <StarBorderRoundedIcon key={i} sx={{ color: 'action.disabled', fontSize: 18 }} />
                  ))
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {avgReviewRating.toFixed(2)} ({reviewCount} {t('reviews.reviews', 'reviews')})
              </Typography>
            </Stack>

            <Box sx={{ mt: 1 }}>
              <TutorSessionDiscoveryMeta
                supportedSessionTypes={tutor.supportedSessionTypes}
                studentLessonCities={tutor.studentLessonCities}
              />
            </Box>

            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip
                size="small"
                color={hostTutorStatusColor(tutor.status)}
                label={statusLabel}
                sx={{ height: 24, fontWeight: 700, fontSize: '0.68rem' }}
              />
              {tutor.calendarId?.trim() ? (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={t('hostMyTutors.calendarLinked', 'Calendar linked')}
                  sx={{
                    height: 24,
                    borderColor: alpha('#4E67EB', 0.35),
                    color: 'text.secondary',
                    '& .MuiChip-icon': { color: 'primary.light' },
                  }}
                />
              ) : null}
              {lessonCount > 0 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('hostMyTutors.lessonOptionsCount', '{{count}} lesson options', { count: lessonCount })}
                  sx={{ height: 24, borderColor: 'divider', color: 'text.secondary' }}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>

        {skills.length > 0 ? (
          <Box>
            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
              <MusicNoteRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="overline" color="text.secondary">
                {t('tutorSearch.skills', 'Skills')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {skills.map((skill) => (
                <Chip
                  key={`${tutor.tutorId}-skill-${skill}`}
                  size="small"
                  label={tutorSkillLabel(t, skill)}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {languages.length > 0 ? (
          <Box>
            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
              <TranslateRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="overline" color="text.secondary">
                {t('tutorSearch.languages', 'Languages')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {languages.map((code) => (
                <Chip
                  key={`${tutor.tutorId}-lang-${code}`}
                  size="small"
                  label={resolveLanguageLabel(code, i18n.language)}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {hasScheduleDetails ? (
          <>
            <IconButton
              size="small"
              aria-expanded={expanded}
              aria-label={
                expanded
                  ? t('hostMyTutors.collapseDetails', 'Hide details')
                  : t('hostMyTutors.expandDetails', 'Show details')
              }
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              sx={{
                position: 'absolute',
                right: 10,
                bottom: 8,
                zIndex: 1,
                color: 'text.secondary',
              }}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Stack spacing={1.25} sx={{ pt: 0.25, pb: 3.5 }}>
                {tutor.overview?.trim() ? (
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {tutor.overview.trim()}
                  </Typography>
                ) : null}
                <TutorWorkingHoursSummary
                  workingHours={tutor.workingHours}
                  timezone={tutor.timezone}
                />
              </Stack>
            </Collapse>
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

export default HostTutorRosterCard;
